import { Link, getRouteApi, useNavigate } from '@tanstack/react-router'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { ShieldAlert } from 'lucide-react'

import { AuthLayout } from '@/routes/login'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/field'
import { useToast } from '@/components/ui/toast'
import { useResetPassword } from '@/features/auth/api'
import { ApiError } from '@/lib/api'

const schema = z
  .object({
    password: z.string().min(8, 'Kata sandi minimal 8 karakter.').max(128, 'Kata sandi terlalu panjang.'),
    confirm: z.string().min(1, 'Ulangi kata sandi baru Anda.'),
  })
  // Konfirmasi diminta karena kolom ini tersembunyi saat diketik; salah ketik
  // di sini berarti terkunci lagi, dan pengguna baru menyadarinya saat gagal
  // masuk.
  .refine((values) => values.password === values.confirm, {
    path: ['confirm'],
    message: 'Kedua kata sandi belum sama.',
  })

type FormValues = z.infer<typeof schema>

const route = getRouteApi('/atur-ulang-sandi')

export function ResetPasswordPage() {
  const { token } = route.useSearch()
  const reset = useResetPassword()
  const navigate = useNavigate()
  const toast = useToast()

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { password: '', confirm: '' },
  })

  if (!token) {
    return (
      <AuthLayout title="Tautan tidak lengkap" subtitle="Alamat yang Anda buka tidak memuat token.">
        <div className="rounded-2xl border border-line p-5">
          <span className="flex size-11 items-center justify-center rounded-xl bg-warning-soft text-warning">
            <ShieldAlert className="size-5.5" />
          </span>
          <p className="mt-4 leading-relaxed text-muted">
            Salin ulang seluruh tautan dari surel yang Anda terima, atau minta tautan baru.
          </p>
          <Link to="/lupa-sandi" className="mt-5 inline-block">
            <Button variant="outline">Minta tautan baru</Button>
          </Link>
        </div>
      </AuthLayout>
    )
  }

  const submit = form.handleSubmit((values) => {
    reset.mutate(
      { token, password: values.password },
      {
        onSuccess: () => {
          toast.success('Kata sandi diganti', 'Silakan masuk dengan kata sandi baru Anda.')
          navigate({ to: '/masuk' })
        },
        onError: (error) =>
          toast.error('Gagal mengganti kata sandi', error instanceof ApiError ? error.message : undefined),
      },
    )
  })

  return (
    <AuthLayout title="Pilih kata sandi baru" subtitle="Setelah diganti, seluruh perangkat akan dikeluarkan.">
      <form onSubmit={submit} className="space-y-4" noValidate>
        <Input
          label="Kata sandi baru"
          type="password"
          autoComplete="new-password"
          placeholder="Minimal 8 karakter"
          error={form.formState.errors.password?.message}
          {...form.register('password')}
        />

        <Input
          label="Ulangi kata sandi baru"
          type="password"
          autoComplete="new-password"
          placeholder="Ketik ulang untuk memastikan"
          error={form.formState.errors.confirm?.message}
          {...form.register('confirm')}
        />

        <Button type="submit" fullWidth size="lg" loading={reset.isPending}>
          Simpan kata sandi baru
        </Button>
      </form>
    </AuthLayout>
  )
}
